/**
 * FactoryReceiveQualityDialog — bám 1:1 cấu trúc Access form
 * `frmFactoryQryReceiveAddQC` (form_frmFactoryQryReceiveAddQC.txt).
 *
 * Layout extracted từ control positions (Left/Top):
 *
 * Header (T=12 → 2652) — 3-col grid:
 *   Row1 (T=12):   grn_id            | date_in              | warehouse
 *   Row2 (T=312):  order_no          | suppliers            | truck_no
 *   Row3 (T=612):  quality_code      | kg_per_bag           | origin
 *   Row4 (T=912):  package           | no_of_bag            | gross_quantity
 *   Row5 (T=1212): stack_id          | area                 | net_quantity_kg
 *   Row6 (T=1788): inspection_company| qc_name              | factory_code
 *   Row7 (T=2076): allowance         | allowance_quantity   | (empty)
 *   Row8 (T=2364): allowance_note (full width)
 *   Row9 (T=2652): deduction_quantity_per | deduction_quantity_kg | payment_qty
 *   Row10(T=3276): grn_id_copy (offset cột 2-3)
 *
 * Quality Grid (T=3684 →) — 2 panels side-by-side, mỗi panel 4 cột
 *   [Field name | Real (%) | Standard (%) | Deduction (%)]:
 *
 *   LEFT  (L=2712,4428,6144)         | RIGHT (L=10980,12696,14412)
 *   T=3948 mc_on_kett                | triages_defects (computed sum)
 *   T=4260 mc                        | fb
 *   T=4572 blacks                    | overdry
 *   T=4884 brokens                   | cherry
 *   T=5196 black_and_broken (sum)    | yellowbeans
 *   T=5508 fm                        | polish_degree
 *   T=5820 mouldy                    | s20
 *   T=6132 brown                     | s19
 *   T=6444 partly_black              | (empty)
 *   T=6756 bleach                    | s18
 *   T=7068 spongy_beans              | s16
 *   T=7380 immature                  | s15
 *   T=7692 old_crop                  | (empty)
 *   T=8004 broken_over_1_per_2       | s14
 *   T=8316 pulper_nipped_beans       | s13
 *   T=8628 blotchy_beans             | s12
 *   T=8940 waxy_beans                | under_s12
 *   T=9252 insect_beans              | cuptest
 *
 * Events (Access AfterUpdate):
 *  - blacks+brokens → black_and_broken (auto-sum, BlackBroken line 11926).
 *  - 16 bean fields → triages_defects (TriagesDefects line 11930).
 *  - stack_id → BE lookup + lock quality_code/area/origin/package.
 *  - suppliers → switch order_no source.
 *  - btnSave: validate allowance_note required if allowance set.
 *  - btnSavePr: Save + OpenReport. Disable khi suppliers='PROCESSING'.
 *
 * Standard/Deduction columns: auto-fill via BE `getQualityReferences`
 * (mirror Access `look_parameter_standard()` + `look_parameter_real_quality()`).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'

import type { FactoryCell } from '@/store/api/factoryApi'
import {
  useGetInspectionCompaniesQuery,
  useGetQualityReferencesMutation,
  useGetReceiveGrnListQuery,
  useLazyGetReceiveQualityQuery,
  useLazyGetReceiveStackQcLookupQuery,
  useLazyGetReceiveOrderNoOptionsQuery,
} from '@/store/api/factoryApi'
import { TEXT_TRUNCATE } from '@/theme/responsive'

export type QualityDialogMode = 'new' | 'edit' | 'view'

export interface FactoryReceiveQualityDialogProps {
  open: boolean
  mode: QualityDialogMode
  factoryCode: string
  initialData: Record<string, FactoryCell> | null
  saving: boolean
  onClose: () => void
  onSave: (payload: Record<string, FactoryCell | undefined>) => void
  onSaveAndPrint?: (payload: Record<string, FactoryCell | undefined>) => void
}

/** Editable set per Access Set_Enabled line 11757. */
const EDITABLE = new Set([
  'mc_on_kett', 'mc', 'blacks', 'brokens', 'fm', 'mouldy', 'brown', 'bleach',
  'spongy_beans', 'partly_black', 'immature', 'old_crop',
  'broken_over_1_per_2', 'pulper_nipped_beans', 'blotchy_beans', 'waxy_beans',
  'insect_beans', 'fb', 'overdry', 'cherry', 'yellowbeans', 'polish_degree',
  's20', 's19', 's18', 's16', 's15', 's14', 's13', 's12', 'under_s12', 'cuptest',
  'qc_name', 'inspection_company', 'allowance', 'allowance_note',
])

/** Bean fields trigger TriagesDefects sum (Access line 11930). */
const TRIAGES_INPUTS = [
  'black_and_broken', 'fm', 'mouldy', 'brown', 'bleach', 'partly_black',
  'immature', 'broken_over_1_per_2', 'pulper_nipped_beans', 'blotchy_beans',
  'waxy_beans', 'insect_beans', 'fb', 'overdry', 'yellowbeans', 'spongy_beans',
]

const STRING_FIELDS = new Set([
  'grn_id', 'date_in', 'truck_no', 'qc_name', 'inspection_company',
  'allowance_note', 'cuptest', 'quality_code', 'suppliers', 'order_no',
  'package', 'origin', 'area', 'stack_id', 'warehouse', 'factory_code',
])

/** Receive header rows (T=12→1212) — 5 rows × 3 cols, all read-only. */
const RECEIVE_ROWS: Array<Array<{ field: string; label: string }>> = [
  [
    { field: 'grn_id', label: 'GRN ID' },
    { field: 'date_in', label: 'Date In' },
    { field: 'warehouse', label: 'Warehouse' },
  ],
  [
    { field: 'order_no', label: 'Order No' },
    { field: 'suppliers', label: 'Suppliers' },
    { field: 'truck_no', label: 'Truck No' },
  ],
  [
    { field: 'quality_code', label: 'Quality Code' },
    { field: 'kg_per_bag', label: 'Kg Per Bag' },
    { field: 'origin', label: 'Origin' },
  ],
  [
    { field: 'package', label: 'Package' },
    { field: 'no_of_bag', label: 'No Of Bag' },
    { field: 'gross_quantity', label: 'Gross Quantity' },
  ],
  [
    { field: 'stack_id', label: 'Stack ID' },
    { field: 'area', label: 'Area' },
    { field: 'net_quantity_kg', label: 'Net Quantity Kg' },
  ],
]

/** Quality grid — 2 parallel panels. `null` = empty row (gap T=6444/7692
 *  in right panel). Each entry là field name; label lấy từ BEAN_LABELS. */
const GRID_ROWS: Array<[string | null, string | null]> = [
  ['mc_on_kett', 'triages_defects'],
  ['mc', 'fb'],
  ['blacks', 'overdry'],
  ['brokens', 'cherry'],
  ['black_and_broken', 'yellowbeans'],
  ['fm', 'polish_degree'],
  ['mouldy', 's20'],
  ['brown', 's19'],
  ['partly_black', null],
  ['bleach', 's18'],
  ['spongy_beans', 's16'],
  ['immature', 's15'],
  ['old_crop', null],
  ['broken_over_1_per_2', 's14'],
  ['pulper_nipped_beans', 's13'],
  ['blotchy_beans', 's12'],
  ['waxy_beans', 'under_s12'],
  ['insect_beans', 'cuptest'],
]

const COMPUTED_FIELDS = new Set(['black_and_broken', 'triages_defects'])

/** Fields có Standard/Deduction columns HIDDEN trong Access (Visible =
 *  NotDefault trên `<field>_col2` và `<field>_col3` TextBoxes). UI ẩn 2
 *  cột Standard/Deduction cho các field này, chỉ hiện Real. */
const HIDDEN_STANDARD_DEDUCTION = new Set([
  'mc_on_kett',
  'triages_defects',
  'cherry',
  'yellowbeans',
  'polish_degree',
  's15',
  's14',
  'under_s12',
  'cuptest',
  'broken_over_1_per_2',
  'pulper_nipped_beans',
  'blotchy_beans',
  'waxy_beans',
])

/** 21 fields contribute to deduction_quantity_per (Access formula line
 *  5947-5961: sum of `[<field>_col3]` values, NEGATED). Exactly = all bean
 *  /size fields − HIDDEN_STANDARD_DEDUCTION. Kept explicit cho easy audit
 *  vs Access source. */
const DEDUCTION_FIELDS = [
  'black_and_broken',
  'blacks',
  'bleach',
  'brokens',
  'brown',
  'fb',
  'fm',
  'insect_beans',
  'mc',
  'mouldy',
  'overdry',
  's12',
  's13',
  's16',
  's18',
  's19',
  's20',
  'immature',
  'partly_black',
  'spongy_beans',
  'old_crop',
]

/** Fields copied khi user pick GRN từ `grn_id_copy` (Access changeGRN line
 *  11778-11819). 34 quality fields — KHÔNG copy qc_name/inspection_company/
 *  allowance/allowance_note (giữ per-current-GRN). */
const COPY_FIELDS = [
  'mc_on_kett', 'mc', 'blacks', 'brokens', 'black_and_broken',
  'fm', 'mouldy', 'brown', 'partly_black', 'bleach',
  'spongy_beans', 'immature', 'old_crop', 'broken_over_1_per_2',
  'pulper_nipped_beans', 'blotchy_beans', 'waxy_beans', 'insect_beans',
  'triages_defects', 'fb', 'overdry', 'cherry', 'yellowbeans',
  'polish_degree', 's20', 's19', 's18', 's16', 's15', 's14',
  's13', 's12', 'under_s12', 'cuptest',
]

const FIELD_LABELS: Record<string, string> = {
  mc_on_kett: 'MC on Kett',
  mc: 'MC',
  blacks: 'Blacks',
  brokens: 'Brokens',
  black_and_broken: 'Black & Broken',
  fm: 'FM',
  mouldy: 'Mouldy',
  brown: 'Brown',
  partly_black: 'Partly Black',
  bleach: 'Bleach',
  spongy_beans: 'Spongy Beans',
  immature: 'Immature',
  old_crop: 'Previous Crop',
  broken_over_1_per_2: 'Broken > 1/2',
  pulper_nipped_beans: 'Pulper Nipped',
  blotchy_beans: 'Blotchy Beans',
  waxy_beans: 'Waxy Beans',
  insect_beans: 'Insect Beans',
  triages_defects: 'Triages Defects',
  fb: 'FB',
  overdry: 'Overdry',
  cherry: 'Cherry',
  yellowbeans: 'Yellow Beans',
  polish_degree: 'Polish Degree',
  s20: 'S20',
  s19: 'S19',
  s18: 'S18',
  s16: 'S16',
  s15: 'S15',
  s14: 'S14',
  s13: 'S13',
  s12: 'S12',
  under_s12: '< S12',
  cuptest: 'Cup Test',
}

function stringifyCell(v: FactoryCell | undefined): string {
  if (v == null) return ''
  return typeof v === 'string' ? v : String(v)
}

function numOrZero(s: string): number {
  const n = Number((s ?? '').toString().replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

/** Standard format `#,##0.00` (Access Format "#,#00…" Decimals=2) — display +
 *  blur reformat + hydrate pre-format (skill /convert-form §Number). */
function formatStandard(v: string | number | null | undefined): string {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return ''
  const n = Number(s)
  if (!Number.isFinite(n)) return String(v ?? '')
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Header weights = Access Format Standard (2 decimals); no_of_bag = General int. */
const STANDARD_HEADER_FIELDS = new Set(['kg_per_bag', 'gross_quantity', 'net_quantity_kg'])

function toPayloadValue(field: string, raw: string): FactoryCell | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  if (STRING_FIELDS.has(field)) return raw
  const n = Number(trimmed.replace(/,/g, '')) // strip Standard thousand separators
  if (Number.isFinite(n)) return n
  return raw
}

/** Numeric/date field set for the readonly Receive header (Access types per
 *  receive table schema). Format display via `formatNumberDisplay` /
 *  `formatDateDisplay`. */
const NUMBER_DISPLAY_FIELDS = new Set([
  'kg_per_bag',
  'no_of_bag',
  'gross_quantity',
  'net_quantity_kg',
])
const DATE_DISPLAY_FIELDS = new Set(['date_in'])

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** Format `dd-MMM-yyyy` to match Access standard display
 *  (BatchReport/BOM page use same pattern). */
function formatDateDisplay(value: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  return `${day}-${month}-${d.getFullYear()}`
}

/** Format number with `en-US` locale separator. Empty/non-numeric → return
 *  raw để user thấy giá trị gốc (không che). */
function formatNumberDisplay(value: string): string {
  if (!value) return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  // Integer (no fractional part) → no decimals; otherwise allow up to 4.
  return n.toLocaleString('en-US', {
    maximumFractionDigits: Number.isInteger(n) ? 0 : 4,
  })
}

/** Get displayed value for a receive header cell — apply
 *  number/date formatting per field type. */
function receiveDisplayValue(field: string, raw: string): string {
  if (DATE_DISPLAY_FIELDS.has(field)) return formatDateDisplay(raw)
  if (STANDARD_HEADER_FIELDS.has(field)) return formatStandard(raw)
  if (NUMBER_DISPLAY_FIELDS.has(field)) return formatNumberDisplay(raw)
  return raw
}

const sectionHeaderSx: SxProps<Theme> = (theme) => ({
  px: 1.25,
  py: 0.5,
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  borderLeft: 3,
  borderColor: 'primary.main',
  borderRadius: 0.5,
  mt: 1.5,
  mb: 1,
})

const compactSx = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.78rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem', fontWeight: 500 },
}

const gridInputSx = {
  '& .MuiInputBase-root': { minHeight: 28, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.3, fontSize: '0.75rem' },
}

const titleBtnSx = {
  py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2,
  textTransform: 'none' as const,
}

const gridHeaderSx: SxProps<Theme> = (theme) => ({
  px: 0.5,
  py: 0.3,
  fontSize: '0.7rem',
  fontWeight: 700,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
})

const labelCellSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  px: 0.5,
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.primary,
})

export default function FactoryReceiveQualityDialog({
  open,
  mode,
  factoryCode,
  initialData,
  saving,
  onClose,
  onSave,
  onSaveAndPrint,
}: FactoryReceiveQualityDialogProps) {
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [allowanceError, setAllowanceError] = useState('')
  const [qualityRefs, setQualityRefs] = useState<
    Record<string, { standard: number | null; real: number | null }>
  >({})
  const [stackLocks, setStackLocks] = useState<Record<string, boolean>>({})
  const [orderNoOptions, setOrderNoOptions] = useState<string[]>([])

  const isView = mode === 'view'

  /** Refs tới mỗi Real input trong quality grid (key = field name) để Tab dọc
   *  từng column (left panel rồi right panel) thay vì nhảy ngang qua 2 panel. */
  const realInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  /** Thứ tự Tab các ô Real: hết column trái (top→bottom) thì sang column phải
   *  (top→bottom) — 1 chuỗi liên tục. */
  const realTabOrder = useMemo(() => {
    const isEditable = (f: string | null): f is string =>
      !!f && !isView && !COMPUTED_FIELDS.has(f) && EDITABLE.has(f)
    const left = GRID_ROWS.map(([l]) => l).filter(isEditable)
    const right = GRID_ROWS.map(([, r]) => r).filter(isEditable)
    return [...left, ...right]
  }, [isView])

  /** Tab/Shift+Tab di chuyển dọc trong chuỗi Real; ra khỏi đầu/cuối thì để
   *  default xử lý (rời grid). */
  const handleRealKeyDown = (field: string) => (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return
    const idx = realTabOrder.indexOf(field)
    if (idx < 0) return
    const nextIdx = e.shiftKey ? idx - 1 : idx + 1
    if (nextIdx < 0 || nextIdx >= realTabOrder.length) return
    const target = realInputRefs.current[realTabOrder[nextIdx]]
    if (!target) return
    e.preventDefault()
    target.focus()
    target.select()
  }

  const [fetchQualityRefs] = useGetQualityReferencesMutation()
  const [fetchStackLookup] = useLazyGetReceiveStackQcLookupQuery()
  const [fetchOrderNoOptions] = useLazyGetReceiveOrderNoOptionsQuery()
  const [fetchSourceQuality, { isFetching: copyLoading }] =
    useLazyGetReceiveQualityQuery()

  /** Receive GRN picker list — Access `grn_id_copy` RowSource. */
  const grnListQuery = useGetReceiveGrnListQuery(
    { factoryCode },
    { skip: !open || isView },
  )
  const grnOptions = grnListQuery.data ?? []
  const [copyError, setCopyError] = useState('')

  /** Inspection company options — Access ComboBox RowSource line 11738.
   *  Skip ở view mode để khỏi waste call (field disabled). */
  const inspectionQuery = useGetInspectionCompaniesQuery(
    { factoryCode },
    { skip: !open || isView },
  )
  const inspectionOptions = inspectionQuery.data ?? []

  // Suppress unused warnings — exports needed for completeness but not used directly.
  void stackLocks
  void orderNoOptions
  void fetchStackLookup
  void fetchOrderNoOptions

  /** Hydrate draft khi mở dialog hoặc đổi initialData. */
  useEffect(() => {
    if (!open) return
    const next: Record<string, string> = {}
    if (initialData) {
      for (const k of Object.keys(initialData)) next[k] = stringifyCell(initialData[k])
    }
    // Pre-format defect/quality numeric fields → Standard (skill §Number hydrate).
    // Header weights format via receiveDisplayValue; count fields stay raw.
    for (const k of Object.keys(next)) {
      if ((EDITABLE.has(k) || COMPUTED_FIELDS.has(k)) && !STRING_FIELDS.has(k)) {
        next[k] = formatStandard(next[k])
      }
    }
    setDraft(next)
    setAllowanceError('')
    setQualityRefs({})
    setStackLocks({})
  }, [open, mode, initialData])

  /** BlackBroken + TriagesDefects auto-sum (Access line 11926-11932). */
  useEffect(() => {
    const blacks = numOrZero(draft['blacks'] ?? '')
    const brokens = numOrZero(draft['brokens'] ?? '')
    const black_and_broken = blacks + brokens
    let triages = 0
    for (const f of TRIAGES_INPUTS) {
      triages += f === 'black_and_broken' ? black_and_broken : numOrZero(draft[f] ?? '')
    }
    setDraft((prev) => {
      const nextBB = String(black_and_broken)
      const nextTr = String(triages)
      if (prev['black_and_broken'] === nextBB && prev['triages_defects'] === nextTr) return prev
      return { ...prev, black_and_broken: nextBB, triages_defects: nextTr }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draft['blacks'], draft['brokens'], draft['fm'], draft['mouldy'],
    draft['brown'], draft['bleach'], draft['partly_black'], draft['immature'],
    draft['broken_over_1_per_2'], draft['pulper_nipped_beans'], draft['blotchy_beans'],
    draft['waxy_beans'], draft['insect_beans'], draft['fb'], draft['overdry'],
    draft['yellowbeans'], draft['spongy_beans'],
  ])

  /** suppliers_AfterUpdate (Access line 11906). */
  const suppliers = draft['suppliers'] ?? ''
  useEffect(() => {
    if (!open) return
    const s = suppliers.trim()
    if (!s) {
      setOrderNoOptions([])
      return
    }
    void fetchOrderNoOptions({ factoryCode, suppliers: s })
      .unwrap()
      .then((rows) => setOrderNoOptions(rows.map((r) => r.order_no)))
      .catch(() => setOrderNoOptions([]))
  }, [open, suppliers, factoryCode, fetchOrderNoOptions])

  /** Bean Standard/Real lookup. */
  const qualityCode = (draft['quality_code'] ?? '').trim()
  useEffect(() => {
    if (!open || !qualityCode) return
    const fieldValues: Record<string, number> = {}
    for (const [l, r] of GRID_ROWS) {
      for (const f of [l, r]) {
        if (!f || COMPUTED_FIELDS.has(f) || HIDDEN_STANDARD_DEDUCTION.has(f)) continue
        const raw = (draft[f] ?? '').trim()
        if (raw === '') continue
        const n = Number(raw.replace(/,/g, '')) // strip Standard thousand separators
        if (Number.isFinite(n)) fieldValues[f] = n
      }
    }
    const timer = setTimeout(() => {
      void fetchQualityRefs({ factoryCode, qualityCode, fieldValues })
        .unwrap()
        .then((data) => setQualityRefs(data))
        .catch(() => {})
    }, 300)
    return () => clearTimeout(timer)
  }, [open, qualityCode, factoryCode, draft, fetchQualityRefs])

  /** Computed Access expressions:
   *
   *  deduction_quantity_per  (line 5947-5961)
   *    = IIf(suppliers='PROCESSING', 0,
   *          -(SUM of RoundNormal([<field>_col3], 2) for 21 DEDUCTION_FIELDS))
   *    Note: leading "-" — Access negates positive deduction lookups (returned
   *    by look_parameter_real_quality) to a negative quantity_per.
   *
   *  deduction_quantity_kg   (line 5056)
   *    = Round(deduction_quantity_per * net_quantity_kg / 100)
   *
   *  allowance_quantity      (line 6038)
   *    = Round(allowance * net_quantity_kg / 100)
   *
   *  payment_qty             (line 5866-5867)
   *    = net_quantity_kg − IIf(allowance IS NULL, deduction_quantity_kg, allowance_quantity)
   *    Tức là KHI có allowance: payment = net − allowance_qty (skip deduction);
   *    KHI không allowance: payment = net − deduction_qty_kg.
   */
  const computed = useMemo(() => {
    const netKg = numOrZero(String(initialData?.net_quantity_kg ?? draft['net_quantity_kg'] ?? ''))
    const allowanceRaw = (draft['allowance'] ?? '').trim()
    const allowanceIsNull = allowanceRaw === ''
    const allowance = numOrZero(allowanceRaw)
    const supplierProcessing = (suppliers || '').trim().toUpperCase() === 'PROCESSING'

    let deduction_quantity_per = 0
    if (!supplierProcessing) {
      let sum = 0
      for (const f of DEDUCTION_FIELDS) {
        const v = qualityRefs[f]?.real
        if (v != null && Number.isFinite(v)) {
          sum += Math.round(v * 100) / 100  // RoundNormal(.., 2)
        }
      }
      deduction_quantity_per = -sum
    }
    const deduction_quantity_kg = Math.round((deduction_quantity_per * netKg) / 100)
    const allowance_quantity = Math.round((allowance * netKg) / 100)
    const payment_qty = netKg - (allowanceIsNull ? deduction_quantity_kg : allowance_quantity)
    return { deduction_quantity_per, deduction_quantity_kg, allowance_quantity, payment_qty }
  }, [initialData, draft, suppliers, qualityRefs])

  const supplierIsProcessing = useMemo(
    () => (suppliers || '').trim().toUpperCase() === 'PROCESSING',
    [suppliers],
  )

  const saveDisabled = isView || saving
  const savePrintDisabled = isView || supplierIsProcessing || saving || !onSaveAndPrint

  const buildPayload = (): Record<string, FactoryCell | undefined> | null => {
    const allowanceRaw = (draft['allowance'] ?? '').trim()
    const allowanceNoteRaw = (draft['allowance_note'] ?? '').trim()
    if (allowanceRaw !== '' && allowanceNoteRaw === '') {
      setAllowanceError('Allowance note is required when allowance is set')
      return null
    }
    setAllowanceError('')
    const payload: Record<string, FactoryCell | undefined> = {}
    for (const f of EDITABLE) {
      payload[f] = toPayloadValue(f, draft[f] ?? '')
    }
    payload['black_and_broken'] = numOrZero(draft['black_and_broken'] ?? '')
    payload['triages_defects'] = numOrZero(draft['triages_defects'] ?? '')
    payload['deduction_quantity_per'] = computed.deduction_quantity_per
    payload['deduction_quantity_kg'] = computed.deduction_quantity_kg
    payload['allowance_quantity'] = computed.allowance_quantity
    return payload
  }

  const handleSave = () => {
    if (saveDisabled) return
    const payload = buildPayload()
    if (!payload) return
    onSave(payload)
  }
  const handleSaveAndPrint = () => {
    if (savePrintDisabled || !onSaveAndPrint) return
    const payload = buildPayload()
    if (!payload) return
    onSaveAndPrint(payload)
  }

  const dialogTitle = useMemo(() => {
    if (mode === 'view') return "Factory - Receive's Quality - View"
    if (mode === 'edit') return "Factory - Receive's Quality (Re-enter)"
    return "Factory - Receive's Quality"
  }, [mode])

  /** Render 1 receive header cell — readonly. Number/Date fields format
   *  display via locale (dd-MMM-yyyy / en-US thousand separator); right-align
   *  numeric. */
  const renderReceiveCell = (cell: { field: string; label: string }) => {
    const raw = draft[cell.field] ?? ''
    const isNumber = NUMBER_DISPLAY_FIELDS.has(cell.field)
    return (
      <TextField
        key={cell.field}
        label={cell.label}
        size="small"
        value={receiveDisplayValue(cell.field, raw)}
        disabled
        fullWidth
        sx={compactSx}
        InputProps={{ readOnly: true }}
        inputProps={isNumber ? { style: { textAlign: 'right' } } : undefined}
      />
    )
  }

  /** Render 1 panel cell trong quality grid — 4 cột: label | real | std | ded.
   *  Standard/Deduction render readonly TextField (như Access TextBox, không
   *  phải chip). Size fields chỉ có Real (Standard/Deduction empty).
   *  Computed/readonly cells display với en-US locale; editable Real input
   *  giữ raw value để user gõ tự nhiên. */
  const renderGridCell = (field: string | null) => {
    if (!field) {
      // Empty slot (T=6444, T=7692 trong right panel).
      return (
        <>
          <Box />
          <Box />
          <Box />
          <Box />
        </>
      )
    }
    const label = FIELD_LABELS[field] ?? field
    const value = draft[field] ?? ''
    const refs = qualityRefs[field]
    const isComputed = COMPUTED_FIELDS.has(field)
    const hideStdDed = HIDDEN_STANDARD_DEDUCTION.has(field)
    const editable = !isView && !isComputed && EDITABLE.has(field)
    // Standard 2-decimal (Access Format) cho ref Standard/Deduction.
    const fmt = (v: number | null | undefined) =>
      v == null || !Number.isFinite(v) ? '' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const isStringField = STRING_FIELDS.has(field)
    // Editable number: raw khi gõ, blur → Standard. Computed/view: Standard display.
    const realDisplay = isStringField || editable ? value : formatStandard(value)
    return (
      <>
        <Box sx={labelCellSx}>{label}</Box>
        <TextField
          size="small"
          type="text"
          value={realDisplay}
          inputRef={
            editable
              ? (el: HTMLInputElement | null) => {
                  realInputRefs.current[field] = el
                }
              : undefined
          }
          onKeyDown={editable ? handleRealKeyDown(field) : undefined}
          onChange={(e) =>
            editable
              ? setDraft((prev) => ({ ...prev, [field]: e.target.value }))
              : undefined
          }
          onBlur={
            editable && !isStringField
              ? () =>
                  setDraft((prev) => {
                    const f = formatStandard(prev[field] ?? '')
                    return f === (prev[field] ?? '') ? prev : { ...prev, [field]: f }
                  })
              : undefined
          }
          disabled={!editable}
          sx={gridInputSx}
          InputProps={isComputed ? { readOnly: true } : undefined}
          inputProps={isStringField ? undefined : { style: { textAlign: 'right' }, inputMode: 'decimal' }}
        />
        {hideStdDed ? (
          <Box />
        ) : (
          <TextField
            size="small"
            value={fmt(refs?.standard)}
            disabled
            sx={gridInputSx}
            InputProps={{ readOnly: true }}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        )}
        {hideStdDed ? (
          <Box />
        ) : (
          <TextField
            size="small"
            value={fmt(refs?.real)}
            disabled
            sx={gridInputSx}
            InputProps={{ readOnly: true }}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        )}
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxHeight: 'min(960px, 95vh)',
          width: 'min(1340px, 96vw)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={dialogTitle}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {dialogTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {!isView && (
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={titleBtnSx}
            >
              {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
          )}
          {!isView && onSaveAndPrint && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSaveAndPrint}
              disabled={savePrintDisabled}
              sx={titleBtnSx}
            >
              Save & Print
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            {isView ? 'Close' : 'Cancel'}
          </Button>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1, overflowY: 'auto', flex: '1 1 auto' }}>
        {supplierIsProcessing && !isView ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 1,
              py: 0.5,
              mb: 1,
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
              border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
              borderRadius: 0.5,
              fontWeight: 600,
              color: 'warning.dark',
            }}
          >
            Save &amp; Print is disabled for PROCESSING suppliers.
          </Typography>
        ) : null}
        {allowanceError ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 1,
              py: 0.5,
              mb: 1,
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
              border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
              borderRadius: 0.5,
              fontWeight: 600,
              color: 'error.dark',
            }}
          >
            {allowanceError}
          </Typography>
        ) : null}

        {/* RECEIVE — rows 1-5 (Access T=12 → 1212), 3-col grid, all read-only. */}
        <Box sx={sectionHeaderSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.04 }}>
            RECEIVE
          </Typography>
        </Box>
        <Stack spacing={1}>
          {RECEIVE_ROWS.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
                gap: 1,
              }}
            >
              {row.map(renderReceiveCell)}
            </Box>
          ))}
        </Stack>

        {/* QUALITY INFORMATION — Access rows 6-10. */}
        <Box sx={sectionHeaderSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.04 }}>
            QUALITY INFORMATION
          </Typography>
        </Box>
        <Stack spacing={1}>
          {/* R6 (T=1788): inspection_company | qc_name | [factory_code hidden].
              Access ComboBox `inspection_company` RowSource = service table
              (line 11738), LimitToList=True → FE Autocomplete strict, chỉ chọn
              trong list (không freeSolo). */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
              gap: 1,
            }}
          >
            <Autocomplete<typeof inspectionOptions[number], false, false, false>
              size="small"
              options={inspectionOptions}
              loading={inspectionQuery.isFetching}
              // LimitToList=True → chỉ chọn trong list; value là option object
              // (hoặc null khi chưa/không khớp code đang lưu).
              value={
                inspectionOptions.find(
                  (o) => o.service_code === (draft['inspection_company'] ?? ''),
                ) ?? null
              }
              getOptionLabel={(o) => {
                const code = o.service_code ?? ''
                const name = o.short_name ?? ''
                return name ? `${code} — ${name}` : code
              }}
              isOptionEqualToValue={(opt, val) => opt.service_code === val.service_code}
              filterOptions={(opts, state) => {
                const needle = state.inputValue.trim().toUpperCase()
                if (!needle) return opts.slice(0, 200)
                return opts
                  .filter((o) => {
                    const hay = `${o.service_code} ${o.service_type ?? ''} ${o.short_name ?? ''}`
                    return hay.toUpperCase().includes(needle)
                  })
                  .slice(0, 200)
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.service_code}>
                  <Box sx={{ display: 'flex', gap: 1.5, width: '100%', fontSize: '0.78rem' }}>
                    <Box sx={{ flex: '0 0 28%', fontWeight: 600 }}>{option.service_code}</Box>
                    <Box sx={{ flex: '0 0 28%', color: 'text.secondary' }}>
                      {option.service_type ?? '-'}
                    </Box>
                    <Box sx={{ flex: 1, color: 'text.secondary' }}>
                      {option.short_name ?? '-'}
                    </Box>
                  </Box>
                </li>
              )}
              onChange={(_, v) => {
                setDraft((prev) => ({ ...prev, inspection_company: v?.service_code ?? '' }))
              }}
              disabled={isView}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Inspection Company"
                  sx={compactSx}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {inspectionQuery.isFetching ? <CircularProgress size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="QC Name"
              size="small"
              value={draft['qc_name'] ?? ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, qc_name: e.target.value }))}
              disabled={isView}
              fullWidth
              sx={compactSx}
            />
            <Box />
          </Box>
          {/* R7 (T=2076): allowance | allowance_quantity | empty */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
              gap: 1,
            }}
          >
            <TextField
              label="Allowance (%)"
              size="small"
              type="text"
              value={draft['allowance'] ?? ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, allowance: e.target.value }))}
              onBlur={() =>
                setDraft((prev) => {
                  const f = formatStandard(prev['allowance'] ?? '')
                  return f === (prev['allowance'] ?? '') ? prev : { ...prev, allowance: f }
                })
              }
              disabled={isView}
              fullWidth
              sx={compactSx}
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'decimal' }}
            />
            <TextField
              label="Allowance Quantity"
              size="small"
              value={Number.isFinite(computed.allowance_quantity) ? formatStandard(computed.allowance_quantity) : ''}
              disabled
              fullWidth
              sx={compactSx}
              InputProps={{ readOnly: true }}
            />
            <Box />
          </Box>
          {/* R8 (T=2364): allowance_note (full-width) */}
          <TextField
            label="Allowance Note"
            size="small"
            value={draft['allowance_note'] ?? ''}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, allowance_note: e.target.value }))
            }
            disabled={isView}
            fullWidth
            multiline
            minRows={2}
            sx={compactSx}
          />
          {/* R9 (T=2652): deduction_quantity_per | deduction_quantity_kg | payment_qty */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
              gap: 1,
            }}
          >
            <TextField
              label="Deduction Quantity (%)"
              size="small"
              value={Number.isFinite(computed.deduction_quantity_per) ? formatStandard(computed.deduction_quantity_per) : ''}
              disabled
              fullWidth
              sx={compactSx}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Deduction Quantity (Kg)"
              size="small"
              value={Number.isFinite(computed.deduction_quantity_kg) ? formatStandard(computed.deduction_quantity_kg) : ''}
              disabled
              fullWidth
              sx={compactSx}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Payment Qty"
              size="small"
              value={Number.isFinite(computed.payment_qty) ? formatStandard(computed.payment_qty) : ''}
              disabled
              fullWidth
              sx={compactSx}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Stack>

        {/* QUALITY GRID — 2 panels side-by-side, mỗi panel 4 cột:
            [Field | Real (%) | Standard (%) | Deduction (%)].
            Header row đầu, sau đó 18 data rows. Combo "Copy Quality from another
            GRN" (Access grn_id_copy R10, changeGRN copy 34 field) đặt CÙNG DÒNG
            với header này, bên phải. */}
        <Box
          sx={
            [
              sectionHeaderSx,
              { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' },
            ] as SxProps<Theme>
          }
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, letterSpacing: 0.04, flex: '1 1 auto', minWidth: 200 }}
          >
            QUALITY GRID — Real / Standard / Deduction
          </Typography>
          {!isView ? (
            <Box sx={{ flex: '0 1 440px', minWidth: 240 }}>
              <Autocomplete<typeof grnOptions[number], false, false, false>
                size="small"
                options={grnOptions}
                loading={grnListQuery.isFetching}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.grn_id ?? '')}
                isOptionEqualToValue={(a, b) => a.grn_id === b.grn_id}
                filterOptions={(opts, state) => {
                  const needle = state.inputValue.trim().toUpperCase()
                  if (!needle) return opts.slice(0, 200)
                  return opts.filter((o) => {
                    const hay = `${o.suppliers ?? ''} ${o.grn_id}`.toUpperCase()
                    return hay.includes(needle)
                  }).slice(0, 200)
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.grn_id}>
                    <Box sx={{ display: 'flex', gap: 1.5, width: '100%', fontSize: '0.78rem' }}>
                      <Box sx={{ flex: 1, fontWeight: 600 }}>{option.grn_id}</Box>
                      <Box sx={{ flex: 1, color: 'text.secondary' }}>{option.suppliers ?? '-'}</Box>
                      <Box sx={{ flex: 1, color: 'text.secondary' }}>
                        {formatDateDisplay(option.date_in ?? '')}
                      </Box>
                    </Box>
                  </li>
                )}
                onChange={(_, picked) => {
                  if (!picked) return
                  if (picked.grn_id === (draft['grn_id'] ?? '').trim()) {
                    setCopyError('Cannot copy from the same GRN.')
                    return
                  }
                  setCopyError('')
                  void (async () => {
                    try {
                      const src = await fetchSourceQuality({
                        factoryCode,
                        grnId: picked.grn_id,
                      }).unwrap()
                      // Mirror Access changeGRN — copy 34 quality fields.
                      setDraft((prev) => {
                        const next = { ...prev }
                        for (const f of COPY_FIELDS) {
                          next[f] = stringifyCell(src[f])
                        }
                        return next
                      })
                    } catch {
                      setCopyError('Could not load source GRN quality.')
                    }
                  })()
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Copy Quality from another GRN"
                    placeholder="Pick source GRN…"
                    sx={compactSx}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {copyLoading || grnListQuery.isFetching ? (
                            <CircularProgress size={14} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {copyError ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 0.25, fontWeight: 600 }}
                >
                  {copyError}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: '1.1fr 0.9fr 0.9fr 0.9fr 1.1fr 0.9fr 0.9fr 0.9fr',
              md: '1.1fr 0.9fr 0.9fr 0.9fr 1.1fr 0.9fr 0.9fr 0.9fr',
            },
            columnGap: 0.75,
            rowGap: 0.5,
            alignItems: 'center',
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            borderRadius: 0.5,
            p: 0.75,
          }}
        >
          {/* Column header row (T=3684 in Access). */}
          <Box sx={gridHeaderSx}>Field</Box>
          <Box sx={gridHeaderSx}>Real (%)</Box>
          <Box sx={gridHeaderSx}>Standard (%)</Box>
          <Box sx={gridHeaderSx}>Deduction (%)</Box>
          <Box sx={gridHeaderSx}>Field</Box>
          <Box sx={gridHeaderSx}>Real (%)</Box>
          <Box sx={gridHeaderSx}>Standard (%)</Box>
          <Box sx={gridHeaderSx}>Deduction (%)</Box>

          {/* 18 data rows — pairs of (left field, right field). */}
          {GRID_ROWS.map(([l, r], i) => (
            <Box key={i} sx={{ display: 'contents' }}>
              {renderGridCell(l)}
              {renderGridCell(r)}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
